import {
  DbJsonB,
  DbTimestampGenerated,
  DbTimestampNullable,
} from '@harvestiq/utils';
import { zTimestamp } from '@harvestiq/zod';
import { expectTypeOf } from 'expect-type';
import { Insertable, Selectable, Updateable } from 'kysely';
import { z } from '@harvestiq/zod';
import { ColorScheme, colorSchemeSchema } from './color-scheme';
import { SlackChannels } from '@harvestiq/constants';

export type AppSettingsTable = {
  // ID is not a UUID, but a string (name) that is unique across all orgs
  id: string;
  createdAt: DbTimestampGenerated;
  updatedAt: DbTimestampGenerated;
  name: string;
  fullLogo: string;
  compactLogo: string;
  favicon: string;
  loadingIndicator: string;
  phone: string;
  email: string;
  domain: string;
  sendgridDomainId: string | null;
  // TODO: Figure out what this is and type it
  sendgridDomainDns: DbJsonB<{ [key: string]: { valid: boolean } } | null>;
  colorScheme: DbJsonB<ColorScheme>;
  isActive: boolean | null;
  isAdmin: boolean | null;
  gaTrackingId: string | null;
  hubspotAccountId: string | null;
  hubspotAccessToken: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  senderEmail: string;
  timezone: string | null;
  fmhApiKey: string | null;
  fmhSigningKey: string | null;
  organizationType: string;
  parentOrgId: string | null;
  stockguardSlackChannel: SlackChannels | null;
  enableNotificationCrons: boolean | null;
  enableClaimsUi: boolean | null;
  agWorksUsername: string | null;
  agWorksAlias: string | null;
  agWorksPassword: string | null;
  enableSMSCampaigns: boolean;
  isOrderButtonHidden: boolean;
  orderButtonText: string;
  twilioSmsSenderNumber: string | null;
  enableCombinedQuoteCampaignsToSingleEmail: boolean;
  enableAggregateReports: boolean | null;
  disabledAt: DbTimestampNullable;
  enableCustomSMS: boolean | null;
  agencyCampaignMessage: string | null;
};

export type DbAppSettings = Selectable<AppSettingsTable>;
export type DbAppSettingsInsert = Insertable<AppSettingsTable>;
export type DbAppSettingsUpdate = Updateable<AppSettingsTable>;

export const dbAppSettingsSchema = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  name: z.string(),
  fullLogo: z.string(), // url to logo
  compactLogo: z.string(), // url to logo
  favicon: z.string(), // url to favicon
  loadingIndicator: z.string(),
  phone: z.string(),
  email: z.string().email(),
  domain: z.string(),
  sendgridDomainId: z.string().nullable(),
  // TODO: Figure out what this is and fully type it
  sendgridDomainDns: z
    .record(z.string(), z.object({ valid: z.boolean() }))
    .nullable(),
  colorScheme: colorSchemeSchema,
  isActive: z.boolean().nullable(),
  isAdmin: z.boolean().nullable(),
  gaTrackingId: z.string().nullable(),
  hubspotAccountId: z.string().nullable(),
  hubspotAccessToken: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  senderEmail: z.string().email(),
  timezone: z.string().nullable(),
  fmhApiKey: z.string().nullable(),
  fmhSigningKey: z.string().nullable(),
  organizationType: z.string(),
  parentOrgId: z.string().nullable(),
  stockguardSlackChannel: z.nativeEnum(SlackChannels).nullable(),
  enableNotificationCrons: z.boolean().nullable(),
  enableClaimsUi: z.boolean().nullable(),
  agWorksUsername: z.string().nullable(),
  agWorksAlias: z.string().nullable(),
  agWorksPassword: z.string().nullable(),
  enableSMSCampaigns: z.boolean(),
  isOrderButtonHidden: z.boolean(),
  orderButtonText: z.string(),
  twilioSmsSenderNumber: z.string().nullable(),
  enableCombinedQuoteCampaignsToSingleEmail: z.boolean(),
  enableAggregateReports: z.boolean().nullable(),
  disabledAt: zTimestamp().nullable(),
  enableCustomSMS: z.boolean().nullable(),
  agencyCampaignMessage: z.string().nullable(),
});
type DbAppSettingsSchemaType = z.output<typeof dbAppSettingsSchema>;
// Ensure the output of the READ schema matches the SELECT schema
expectTypeOf<DbAppSettings>().toMatchTypeOf<DbAppSettingsSchemaType>();
// Ensure keys match
expectTypeOf<keyof DbAppSettingsSchemaType>().toEqualTypeOf<
  keyof DbAppSettings
>();

export const dbAppSettingsInsertSchema = dbAppSettingsSchema
  .partial()
  .required({ id: true, organizationType: true })
  .extend({
    createdAt: zTimestamp().optional(),
    updatedAt: zTimestamp().optional(),
  });
type DbAppSettingsInsertSchemaType = z.output<typeof dbAppSettingsInsertSchema>;
// Ensure types match
expectTypeOf<DbAppSettingsInsert>().toMatchTypeOf<DbAppSettingsInsertSchemaType>();
// Ensure keys match
expectTypeOf<keyof DbAppSettingsInsertSchemaType>().toEqualTypeOf<
  keyof DbAppSettingsInsert
>();

export const dbAppSettingsUpdateSchema = dbAppSettingsInsertSchema.partial();
type DbAppSettingsUpdateSchemaType = z.input<typeof dbAppSettingsUpdateSchema>;
// Ensure types match
expectTypeOf<DbAppSettingsUpdate>().toMatchTypeOf<DbAppSettingsUpdateSchemaType>();
// Ensure keys match
expectTypeOf<keyof DbAppSettingsUpdateSchemaType>().toEqualTypeOf<
  keyof DbAppSettingsUpdate
>();
